import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "src/store/features/api";
import { AnyObject } from "yup/lib/types";
import { useAppDispatch } from "src/store/store";
import {
  RegisteredOfficeAddressType,
  setRegisteredOfficeAddress,
} from "src/store/features/onboarding.slice";
import { Grid, InputAdornment, List, ListItemButton } from "@mui/material";
import { SearchAddress, StyledTextField } from "./styled-components/AddressFields";
import { Field, useFormikContext } from "formik";
import { GridCheckCircleIcon } from "@mui/x-data-grid";
import { RegisteredOfficeAddressValues } from "./utils/formSchema";
import _ from "lodash";

interface GoogleAutocompleteProps {
  onPlaceSelected?: (place: any) => void;
}

export const StyledEndAdorment = (props: { selected: boolean }) => {
  return (
    <InputAdornment position="end">
      <div style={{ display: props.selected ? "flex" : "none", width: "100%" }}>
        <GridCheckCircleIcon sx={{ fill: "#002035", fontSize: "16px" }} />
      </div>
    </InputAdornment>
  );
};

const GoogleAutocomplete: React.FC<GoogleAutocompleteProps> = ({ onPlaceSelected }) => {
  const [input, setInput] = useState<string>("");
  const [hide, setHide] = useState<boolean>(false);
  const [delayedInput, setDelayedInput] = useState(input);
  const { data } = api.useLookUpAddressFromInputQuery<{
    data: { predictions: AnyObject[]; status: string };
  }>(
    { input: delayedInput },
    {
      skip: !delayedInput || delayedInput.includes(","),
    },
  );

  const debouncedChangeHandler = useCallback(
    _.debounce((input) => setDelayedInput(input), 300),
    [],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHide(false);
    const inputVal = encodeURIComponent(e.target.value);
    setInput(inputVal);
    debouncedChangeHandler(inputVal);
  };

  const handleSuggestionClick = async (suggestion: any) => {
    setInput(suggestion.description);
    setHide(true);
    if (onPlaceSelected) {
      onPlaceSelected(suggestion);
    }
  };

  const selected = data?.predictions.length > 0;

  return (
    <>
      <Field
        as={SearchAddress}
        selected={selected}
        InputProps={{
          endAdornment: <StyledEndAdorment selected={selected} />,
        }}
        value={decodeURIComponent(input)}
        onChange={handleInputChange}
        placeholder="Search places..."
      />
      {!hide && data?.predictions.length > 0 && (
        <List dense={true}>
          {data.predictions.map((suggestion: any) => (
            <ListItemButton
              key={suggestion.place_id}
              onClick={() => handleSuggestionClick(suggestion)}>
              {suggestion.description}
            </ListItemButton>
          ))}
        </List>
      )}
    </>
  );
};

const RekycAddressLookUp1: React.FC = () => {
  const [getDetails] = api.useGetPlaceDetailMutation();
  const dispatch = useAppDispatch();

  const handlePlaceSelected = (place: any) => {
    // You can further type 'place' based on the Google Places API response.
    const place_id = place.place_id as string;

    getDetails({ getPlaceDetailsDto: { placeid: place_id } })
      .unwrap()
      .then((res: any) => {
        const {
          result: { formatted_address, address_components },
        } = res;

        const getAddressComponent = (type: string, components: any[], abbreviated?: boolean) => {
          const component = components.find((item: any) => item.types.includes(type));
          if (abbreviated) return component ? component.short_name : null;
          return component ? component.long_name : null;
        };

        const details: RegisteredOfficeAddressType = {
          registeredOfficeFullAddress: formatted_address,
          registeredOfficeCountry: getAddressComponent("country", address_components),
          registeredOfficePostcode: getAddressComponent("postal_code", address_components),
          registeredOfficeCity: getAddressComponent("locality", address_components),
          council: getAddressComponent("administrative_area_level_2", address_components),
          registeredOfficeStreet: getAddressComponent("route", address_components),
          registeredOfficeStreetNumber: getAddressComponent("street_number", address_components),
          registeredOfficeunitApartment: getAddressComponent("subpremise", address_components),
          registeredOfficeState: getAddressComponent(
            "administrative_area_level_1",
            address_components,
            true,
          ),
        };
        dispatch(setRegisteredOfficeAddress(details));
      });
  };

  const { values, errors } = useFormikContext<RegisteredOfficeAddressValues>();
  const {
    registeredOfficeCountry: countryErrorMessage,
    registeredOfficeStreet: streetAddressErrorMessage,
    registeredOfficeunitApartment: unitApartmentErrorMessage,
    registeredOfficeState: stateErrorMessage,
    registeredOfficePostcode: postCodeErrorMessage,
  } = errors;

  const {
    registeredOfficeCountry,
    registeredOfficeState,
    registeredOfficePostcode,
    registeredOfficeStreet,
    registeredOfficeunitApartment,
  } = values;

  const prevValues = useRef(values);

  useEffect(() => {
    // Update redux state on manual input after checking for pottential infinite loop
    if (
      prevValues.current.registeredOfficeStreet !== values.registeredOfficeStreet ||
      prevValues.current.registeredOfficeunitApartment !== values.registeredOfficeunitApartment ||
      prevValues.current.registeredOfficeCountry !== values.registeredOfficeCountry ||
      prevValues.current.registeredOfficeState !== values.registeredOfficeState ||
      prevValues.current.registeredOfficePostcode !== values.registeredOfficePostcode
    ) {
      const details = {
        registeredOfficeFullAddress: values.registeredOfficeStreet,
        registeredOfficeCountry: values.registeredOfficeCountry,
        registeredOfficePostcode: values.registeredOfficePostcode,
        registeredOfficeunitApartment: values.registeredOfficeunitApartment,
        registeredOfficeState: values.registeredOfficeState,
      };

      dispatch(setRegisteredOfficeAddress(details));
    }

    prevValues.current = values;
  }, [values, dispatch]);

  return (
    <>
      <Grid container spacing={1} mb={1}>
        <Grid item xs={12} mb={1}>
          <GoogleAutocomplete onPlaceSelected={handlePlaceSelected} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Field
            placeholder="Country of Residence"
            error={!!countryErrorMessage}
            helperText={countryErrorMessage}
            name="registeredOfficeCountry"
            as={StyledTextField}
            selected={!!registeredOfficeCountry}
            InputProps={{
              endAdornment: <StyledEndAdorment selected={!!registeredOfficeCountry} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Field
            placeholder="State"
            error={!!stateErrorMessage}
            helperText={stateErrorMessage}
            as={StyledTextField}
            name="registeredOfficeState"
            selected={!!registeredOfficeState}
            InputProps={{
              endAdornment: <StyledEndAdorment selected={!!registeredOfficeState} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Field
            placeholder="Postcode"
            error={!!postCodeErrorMessage}
            helperText={postCodeErrorMessage}
            as={StyledTextField}
            name="registeredOfficePostcode"
            selected={!!registeredOfficePostcode}
            InputProps={{
              endAdornment: <StyledEndAdorment selected={!!registeredOfficePostcode} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Field
            placeholder="Street Address"
            error={!!streetAddressErrorMessage}
            helperText={streetAddressErrorMessage}
            as={StyledTextField}
            name="registeredOfficeStreet"
            selected={!!registeredOfficeStreet}
            InputProps={{
              endAdornment: <StyledEndAdorment selected={!!registeredOfficeStreet} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Field
            placeholder="(Optional) Unit/Apartment no."
            as={StyledTextField}
            name="registeredOfficeunitApartment"
            error={!!unitApartmentErrorMessage}
            helperText={unitApartmentErrorMessage}
            selected={!!registeredOfficeunitApartment && !unitApartmentErrorMessage}
            InputProps={{
              endAdornment: (
                <StyledEndAdorment
                  selected={!!registeredOfficeunitApartment && !unitApartmentErrorMessage}
                />
              ),
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default RekycAddressLookUp1;
